import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type Bereich } from "@jornado/shared";
import { api, fehlertext } from "./api";
import { beschriftung } from "./baukasten";
import { useBereiche } from "./bereiche";
import { Eingabe, Feld, Fehler, Karte, Knopf, Laedt, Textknopf } from "./ui";

/**
 * Berichtsvorlagen: ein Bericht, der an keinem Objekt haengt.
 *
 * Der Anlass ist ein Betrieb ohne wiederkehrende Aufgabe. Ein Fensterbauer hat
 * keinen Rundgang, den er jede Woche wiederholt; er hat einen Auftrag, faehrt
 * einmal hin und dokumentiert, was er gemacht hat. Um ueberhaupt einen Bericht
 * schreiben zu duerfen, haette er bisher je Baustelle eine Dauercheckliste am
 * Objekt anlegen muessen, die nach dem Einbau nie wieder gebraucht wird.
 *
 * Eine Vorlage steht dagegen einmal im Betrieb. Der Mitarbeiter waehlt sie am
 * Termin, das Objekt kommt vom Termin, und heraus kommt derselbe Bericht wie
 * ueberall: dasselbe PDF, dieselbe Mail, dasselbe Markenkit.
 *
 * Technisch ist es dieselbe Zeile wie eine Objektcheckliste, nur ohne Objekt.
 * Die Begruendung dafuer steht in Migration 0055; kurz gesagt sind beide
 * derselbe Gedanke, einmal an ein Haus gebunden und einmal nicht.
 */

interface Vorlage {
  id: string;
  label: string | null;
  bereiche: Bereich[];
  is_active: boolean;
}

export function Berichtsvorlagen({ darfAendern }: { darfAendern: boolean }) {
  const { t, i18n } = useTranslation();
  const { bereiche: alleBereiche } = useBereiche();
  const [vorlagen, setVorlagen] = useState<Vorlage[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [bearbeitet, setBearbeitet] = useState<Vorlage | null>(null);

  function laden() {
    void api.get<{ vorlagen: Vorlage[] }>("/berichtsvorlagen?alle=ja")
      .then((r) => { setVorlagen(r.vorlagen); setFehler(null); })
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }
  useEffect(laden, []);

  async function stilllegen(v: Vorlage) {
    try {
      await api.patch(`/berichtsvorlagen/${v.id}`, { isActive: !v.is_active });
      laden();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  async function loeschen(v: Vorlage) {
    try {
      await api.del(`/berichtsvorlagen/${v.id}`);
      laden();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  return (
    <Karte>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold">{t("vorlage.titel")}</h2>
        {darfAendern && !neu && !bearbeitet && (
          <Knopf art="neben" onClick={() => setNeu(true)}>{t("vorlage.neu")}</Knopf>
        )}
      </div>
      <p className="mb-4 text-sm text-leise">{t("vorlage.hinweis")}</p>
      <Fehler text={fehler} />

      {(neu || bearbeitet) && (
        <VorlagenFormular
          vorlage={bearbeitet}
          onFertig={() => { setNeu(false); setBearbeitet(null); laden(); }}
          onAbbruch={() => { setNeu(false); setBearbeitet(null); }}
        />
      )}

      {!vorlagen ? <Laedt /> : vorlagen.length === 0 ? (
        <p className="text-sm text-leise">{t("vorlage.keine")}</p>
      ) : (
        <ul className="divide-y divide-linie">
          {vorlagen.map((v) => (
            <li key={v.id} className="flex flex-wrap items-start justify-between gap-3 py-2">
              <span className="min-w-0">
                <span className={`block font-medium ${v.is_active ? "" : "text-leise line-through"}`}>
                  {v.label}
                </span>
                <span className="block text-sm text-leise">
                  {v.bereiche.map((b) =>
                    beschriftung(alleBereiche?.find((x) => x.key === b)?.name_i18n, i18n.language, b)
                  ).join(", ") || t("vorlage.ohneBereich")}
                </span>
              </span>
              {darfAendern && (
                <span className="flex shrink-0 items-center gap-3">
                  <Textknopf onClick={() => { setNeu(false); setBearbeitet(v); }}>
                    {t("aktion.bearbeiten")}
                  </Textknopf>
                  <Textknopf onClick={() => stilllegen(v)}>
                    {v.is_active ? t("baukasten.stilllegen") : t("baukasten.wiederAktiv")}
                  </Textknopf>
                  <Textknopf gefahr onClick={() => loeschen(v)}>{t("aktion.loeschen")}</Textknopf>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Karte>
  );
}

/**
 * Anlegen und Aendern in einem Formular.
 *
 * Die Bezeichnung ist Pflicht, anders als an der Objektcheckliste. Dort
 * benennt das Haus die Liste; hier steht sie fuer sich, und der Mitarbeiter am
 * Termin erkennt nur an diesem Namen, welchen Bericht er beginnt.
 */
function VorlagenFormular({
  vorlage, onFertig, onAbbruch
}: {
  vorlage: Vorlage | null;
  onFertig: () => void;
  onAbbruch: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { bereiche: alleBereiche } = useBereiche();
  const [label, setLabel] = useState(vorlage?.label ?? "");
  const [gewaehlt, setGewaehlt] = useState<Bereich[]>(vorlage?.bereiche ?? []);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const waehlbar = (alleBereiche ?? []).filter((b) => b.is_active || gewaehlt.includes(b.key));

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      const daten = { label: label.trim(), bereiche: gewaehlt };
      if (vorlage) await api.patch(`/berichtsvorlagen/${vorlage.id}`, daten);
      else await api.post("/berichtsvorlagen", daten);
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="mb-4 rounded border border-marke/40 bg-grund/40 p-4">
      <Fehler text={fehler} />
      <Feld label={t("vorlage.bezeichnung")} hinweis={t("vorlage.bezeichnungHinweis")} breit>
        <Eingabe value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder={t("vorlage.beispiel")} />
      </Feld>
      <div className="mt-3">
        {/*
          * Eigener Text und nicht der von der Objektcheckliste: dort heisst er
          * "Bereiche an diesem Objekt", und eine Vorlage hat keins.
          */}
        <span className="mb-2 block text-sm font-medium">{t("vorlage.bereiche")}</span>
        {waehlbar.length === 0 ? (
          <p className="text-sm text-leise">{t("checkliste.keineBereiche")}</p>
        ) : (
          <div className="flex flex-wrap gap-4">
            {waehlbar.map((b) => (
              <label key={b.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="size-5 accent-marke"
                  checked={gewaehlt.includes(b.key)}
                  onChange={(e) =>
                    setGewaehlt(e.target.checked
                      ? [...gewaehlt, b.key]
                      : gewaehlt.filter((x) => x !== b.key))}
                />
                {beschriftung(b.name_i18n, i18n.language, b.key)}
              </label>
            ))}
          </div>
        )}
      </div>
      <div className="mt-4 flex gap-2">
        <Knopf onClick={speichern} disabled={laeuft || !label.trim() || gewaehlt.length === 0}>
          {laeuft ? t("aktion.speichert") : t("aktion.speichern")}
        </Knopf>
        <Knopf art="neben" onClick={onAbbruch}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </div>
  );
}
